import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchAllUserQuotes, getActiveQuoteLines } from "../utils/shopApi";
import { subscribePendingQuotesChanged } from "../hooks/usePendingQuotes";
import {
  STATUS_LABELS,
  STATUS_STYLES,
  filterQuotesByMode,
  normalizeQuoteStatus,
} from "../utils/quoteStatus";
import { NAV } from "../Navbar/navbarTheme";
import AuthFormLayout from "./AuthFormLayout";
import {
  AUTH_PRIMARY,
  authAlertSx,
  authFooterActionsSx,
  authFooterSecondaryButtonSx,
  authPageSx,
  authSecondaryButtonSx,
  authSubmitButtonSx,
} from "./authStyles";

const LINE = NAV.line;

const sectionLabelSx = {
  fontSize: "0.75rem",
  fontWeight: 800,
  color: NAV.inkMuted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  mb: 0.5,
};

const PAGE_SIZE = 10;

const QUOTE_LIST_CONFIG = {
  requested: {
    title: "Requested quotes",
    subtitle: "Track active quote requests awaiting review or pricing.",
    summaryTitle: "Your active requests",
    summaryDescription: (total) =>
      `${total} active quote request${total === 1 ? "" : "s"}`,
    emptyMessage:
      "You have no active quote requests. Browse products and add them to your basket to request a quote.",
    listLabel: "Requests",
    listPath: "/QuoteHistory",
  },
  history: {
    title: "History quotes",
    subtitle:
      "View completed quote requests that have been accepted, rejected, or closed.",
    summaryTitle: "Your quote history",
    summaryDescription: (total) =>
      `${total} completed quote request${total === 1 ? "" : "s"}`,
    emptyMessage: "You have no completed quote requests in your history yet.",
    listLabel: "History",
    listPath: "/QuoteHistory/history",
  },
};

const quoteCardSx = {
  borderRadius: 2,
  border: `1px solid ${LINE}`,
  bgcolor: NAV.chipRowBg,
  overflow: "hidden",
};

function hasLinePrice(line) {
  return line?.unit_price != null && String(line.unit_price).trim() !== "";
}

function parsePrice(value) {
  if (value == null || value === "") return 0;
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ""), 10);
  return Number.isFinite(n) ? n : 0;
}

function formatGBP(amount) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getLineSubtotal(line) {
  if (!hasLinePrice(line)) return null;
  const qty = Number(line?.quantity) || 0;
  const unit = parsePrice(line?.unit_price);
  return qty * unit;
}

function getQuotePricing(lines) {
  const items = Array.isArray(lines) ? lines : [];
  const pricedLines = items.filter(hasLinePrice);
  const total = pricedLines.reduce(
    (sum, line) => sum + (getLineSubtotal(line) ?? 0),
    0,
  );

  return {
    total,
    pricedCount: pricedLines.length,
    lineCount: items.length,
    hasUnpricedLines: pricedLines.length < items.length,
  };
}

function parseWeight(value) {
  if (value == null || value === "") return null;
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

function formatWeight(kg) {
  if (kg == null) return "—";
  return `${new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(kg)} kg`;
}

function getLineTotalWeightKg(line) {
  const unitWeight = parseWeight(line?.product?.gross_weight);
  if (unitWeight == null) return null;
  const qty = Number(line?.quantity) || 0;
  return unitWeight * qty;
}

function getQuoteEstimatedWeight(lines) {
  const items = Array.isArray(lines) ? lines : [];
  let total = 0;
  let hasWeight = false;

  for (const line of items) {
    const lineWeight = getLineTotalWeightKg(line);
    if (lineWeight != null) {
      total += lineWeight;
      hasWeight = true;
    }
  }

  return hasWeight ? total : null;
}

const QuoteCard = ({ quote, listPath }) => {
  const lines = getActiveQuoteLines(quote.lines);
  const statusKey = normalizeQuoteStatus(quote.status);
  const statusLabel = STATUS_LABELS[statusKey] ?? quote.status ?? "Unknown";
  const statusStyle = STATUS_STYLES[statusKey] ?? STATUS_STYLES.pending;
  const pricing = getQuotePricing(lines);
  const estimatedTotalWeight = getQuoteEstimatedWeight(lines);

  const pricingLabel = (() => {
    if (pricing.pricedCount === 0) return null;
    if (pricing.hasUnpricedLines) {
      return `${formatGBP(pricing.total)}+`;
    }
    return formatGBP(pricing.total);
  })();

  return (
    <Box sx={quoteCardSx}>
      <Box sx={{ p: 1.75 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
          sx={{ mb: quote.customer_note || lines.length > 0 ? 1.5 : 0 }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                background: `linear-gradient(135deg, ${AUTH_PRIMARY} 0%, #163a5e 100%)`,
                flexShrink: 0,
              }}
            >
              <ReceiptLongOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.9375rem",
                  color: NAV.ink,
                  lineHeight: 1.35,
                }}
              >
                {quote.reference || `Quote #${quote.id}`}
              </Typography>
              <Typography
                sx={{ fontSize: "0.8125rem", color: NAV.inkMuted, mt: 0.35 }}
              >
                Submitted {formatDate(quote.created_at)}
                {quote.updated_at && quote.updated_at !== quote.created_at
                  ? ` · Updated ${formatDate(quote.updated_at)}`
                  : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            {pricingLabel ? (
              <Chip
                label={pricingLabel}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: NAV.navyMuted,
                  color: AUTH_PRIMARY,
                }}
              />
            ) : lines.length > 0 ? (
              <Chip
                label="Awaiting quote"
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: "rgba(245, 158, 11, 0.12)",
                  color: "#b45309",
                }}
              />
            ) : null}
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                height: 26,
                ...statusStyle.chip,
              }}
            />
          </Stack>
        </Stack>

        {quote.customer_note ? (
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              p: 1.5,
              mb: lines.length > 0 ? 1.5 : 0,
              borderRadius: 2,
              bgcolor: NAV.chipRowBg,
              border: `1px solid ${LINE}`,
            }}
          >
            <NotesOutlinedIcon
              sx={{
                fontSize: 18,
                color: NAV.inkMuted,
                mt: 0.15,
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...sectionLabelSx, mb: 0.25 }}>Note</Typography>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: NAV.inkMuted,
                  lineHeight: 1.6,
                }}
              >
                {quote.customer_note}
              </Typography>
            </Box>
          </Box>
        ) : null}

        {lines.length > 0 ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mb: 1.5,
              py: 1,
              px: 1.25,
              borderRadius: 2,
              bgcolor: NAV.chipRowBg,
              border: `1px solid ${LINE}`,
            }}
          >
            <Typography sx={{ fontWeight: 800, color: AUTH_PRIMARY }}>
              Estimated total weight
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                color: AUTH_PRIMARY,
                fontSize: "1.125rem",
              }}
            >
              {formatWeight(estimatedTotalWeight)}
            </Typography>
          </Stack>
        ) : null}

        {lines.length > 0 ? (
          <Button
            fullWidth
            variant="outlined"
            component={Link}
            to={`/QuoteHistory/${quote.id}`}
            state={{ quote, listPath }}
            endIcon={<OpenInNewOutlinedIcon />}
            sx={{
              ...authSecondaryButtonSx,
              mt: quote.customer_note ? 0 : 0.5,
              mb: 0,
            }}
          >
            View {lines.length} line item
            {lines.length === 1 ? "" : "s"}
          </Button>
        ) : (
          <Typography
            sx={{
              fontSize: "0.8125rem",
              color: NAV.inkMuted,
              fontStyle: "italic",
              mt: 0.5,
            }}
          >
            No line items on this request.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const QuoteHistoryPage = ({ mode = "requested" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSelector((state) => state.auth);
  const config = QUOTE_LIST_CONFIG[mode] ?? QUOTE_LIST_CONFIG.requested;

  const [allQuotes, setAllQuotes] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const filteredQuotes = useMemo(
    () => filterQuotesByMode(allQuotes, mode),
    [allQuotes, mode],
  );

  const paginatedQuotes = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredQuotes.slice(start, start + PAGE_SIZE);
  }, [filteredQuotes, page]);

  const lastPage = Math.max(1, Math.ceil(filteredQuotes.length / PAGE_SIZE));
  const total = filteredQuotes.length;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [mode]);

  useEffect(() => {
    if (!token) {
      navigate("/SignIn", { replace: true });
    }
  }, [token, navigate]);

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const quotes = await fetchAllUserQuotes();
      setAllQuotes(quotes);
    } catch (loadError) {
      if (loadError?.code === "AUTH_REQUIRED") {
        navigate("/SignIn", { replace: true });
        return;
      }
      setError(
        loadError?.message ||
          "Unable to load your quote history right now. Please try again later.",
      );
      setAllQuotes([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!token) return;
    loadQuotes();
  }, [token, loadQuotes]);

  useEffect(() => {
    if (!token) return undefined;
    return subscribePendingQuotesChanged(() => {
      loadQuotes();
    });
  }, [token, loadQuotes]);

  const statusCounts = useMemo(() => {
    return filteredQuotes.reduce((acc, quote) => {
      const key = normalizeQuoteStatus(quote.status) || "pending";
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
  }, [filteredQuotes]);

  if (!token) {
    return null;
  }

  if (loading && allQuotes.length === 0 && !error) {
    return (
      <Box
        component="main"
        sx={{
          ...authPageSx,
          minHeight: "60vh",
        }}
      >
        <CircularProgress sx={{ color: AUTH_PRIMARY }} />
      </Box>
    );
  }

  const currentPage = Math.min(page, lastPage);

  return (
    <AuthFormLayout
      wide
      maxWidth={720}
      title={config.title}
      subtitle={config.subtitle}
      icon={HistoryOutlinedIcon}
      footer={
        <Stack spacing={1.5} sx={authFooterActionsSx}>
          <Button
            component={Link}
            to="/Profile"
            variant="outlined"
            fullWidth
            sx={authFooterSecondaryButtonSx}
          >
            Back to profile
          </Button>
        </Stack>
      }
    >
      {total > 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: NAV.chipRowBg,
            border: `1px solid ${LINE}`,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              background: `linear-gradient(135deg, ${AUTH_PRIMARY} 0%, #163a5e 100%)`,
              flexShrink: 0,
            }}
          >
            <HistoryOutlinedIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 800, color: NAV.ink, lineHeight: 1.3 }}
            >
              {config.summaryTitle}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8125rem",
                color: NAV.inkMuted,
                mt: 0.25,
              }}
            >
              {config.summaryDescription(total)}
            </Typography>
          </Box>
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={authAlertSx}>
          {error}
        </Alert>
      ) : null}

      {!error && filteredQuotes.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography sx={{ color: NAV.inkMuted, mb: 2, lineHeight: 1.6 }}>
            {config.emptyMessage}
          </Typography>
          {mode === "requested" ? (
            <Button
              component={Link}
              to="/products"
              variant="contained"
              sx={authSubmitButtonSx}
            >
              Browse products
            </Button>
          ) : null}
        </Box>
      ) : null}

      {!error && filteredQuotes.length > 0 ? (
        <Stack spacing={2}>
          {Object.keys(statusCounts).length > 1 ? (
            <Box>
              <Typography sx={sectionLabelSx}>Summary</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.entries(statusCounts).map(([key, count]) => {
                  const style = STATUS_STYLES[key] ?? STATUS_STYLES.pending;
                  return (
                    <Chip
                      key={key}
                      label={`${STATUS_LABELS[key] ?? key}: ${count}`}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        ...style.chip,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          ) : null}

          <Box>
            <Typography sx={{ ...sectionLabelSx, mb: 1 }}>
              {config.listLabel}
            </Typography>
            <Stack spacing={1.5}>
              {paginatedQuotes.map((quote) => (
                <QuoteCard
                  key={quote.id}
                  quote={quote}
                  listPath={config.listPath}
                />
              ))}
            </Stack>
          </Box>

          {lastPage > 1 ? (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{
                pt: 0.5,
                px: 0.5,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage <= 1 || loading}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                startIcon={<ChevronLeftIcon />}
                sx={authFooterSecondaryButtonSx}
              >
                Previous
              </Button>
              <Typography sx={{ fontSize: "0.8125rem", color: NAV.inkMuted }}>
                Page {currentPage} of {lastPage}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage >= lastPage || loading}
                onClick={() => setPage((prev) => prev + 1)}
                endIcon={<ChevronRightIcon />}
                sx={authFooterSecondaryButtonSx}
              >
                Next
              </Button>
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </AuthFormLayout>
  );
};

export default QuoteHistoryPage;
